import { useEffect, useRef } from 'react'
import { getBilling, getPaymentOptions } from '@/lib/api/financial/financial-billing-api'
import {
    collectToasts,
    registerScreenController,
    type BillingActionResult,
    type BillingSaveResult,
    type BillingSnapshot,
} from '@/lib/ai-user/screen-bridge'

/**
 * Controlador da tela de edição da cobrança para o Modo IA (ver docs/ai-user.md, "Fase 2,
 * etapa 2b"). É para cá que o agendamento leva quando a forma é decidida depois: o último
 * atendimento de um grupo ("Adicionar outro") e o "dividir o pagamento".
 *
 * Cada ação faz o que o operador faria nos campos da tela (forma principal, parcelas,
 * formas extras) e o salvar é o mesmo "Finalizar". Só na cobrança vinda do agendamento.
 */

/** Formas que a IA lança. Dinheiro, maquininha e outro podem nascer pagos (baixa automática). */
const AI_PAYMENT_METHODS = ['PIX', 'CREDIT_CARD', 'DEBIT_CARD']

/** Formas com link de pagamento (o mesmo critério do `BillingPaymentShare`). */
const CHECKOUT_LINK_METHODS = ['PIX', 'CREDIT_CARD', 'DEBIT_CARD', 'BOLETO']

export interface BillingAiBridgeDeps {
    enabled: boolean
    billingId: number
    status: string | null
    /** Forma principal, parcelas e o valor dela (centavos). */
    paymentMethod: string
    installmentCount: number | null
    principalAmount: number
    /** Formas extras (centavos). */
    parts: Array<{ method: string; amount: number }>
    availableMethods: string[]
    busy: boolean
    setPaymentMethod: (method: string, installments: number | null) => void
    /** Troca as formas extras; a principal fica com o que sobra do total. */
    setParts: (parts: Array<{ method: string; amount: number }>) => void
    /** O mesmo "Finalizar" da tela. */
    save: () => Promise<void>
}

const SETTLE_MS = 500

function checkoutUrlOf(billing: any): string | null {
    const method: string | null = billing?.paymentMethod ?? null
    return billing?.publicToken && (!method || CHECKOUT_LINK_METHODS.includes(method))
        ? `${window.location.origin}/pay/${billing.publicToken}`
        : null
}

export function useBillingAiBridge(deps: BillingAiBridgeDeps): void {
    const depsRef = useRef(deps)
    depsRef.current = deps

    useEffect(() => {
        if (!deps.enabled) return

        const snapshot = (): BillingSnapshot => {
            const d = depsRef.current
            const partsTotal = d.parts.reduce((sum, part) => sum + part.amount, 0)
            return {
                billingId: d.billingId,
                status: d.status,
                total: d.principalAmount + partsTotal,
                principal: {
                    method: d.paymentMethod || null,
                    installments: d.installmentCount,
                    amount: d.principalAmount,
                },
                parts: d.parts,
                availableMethods: d.availableMethods.filter(method => AI_PAYMENT_METHODS.includes(method)),
                ready: !d.busy,
            }
        }

        const run = async (action: () => Promise<string | void> | string | void): Promise<BillingActionResult> => {
            const { result: refusal, toasts } = await collectToasts(async () => {
                const refused = await action()
                if (!refused) await new Promise(resolve => setTimeout(resolve, SETTLE_MS))
                return refused
            })
            const messages = refusal ? [`Erro: ${refusal}`, ...toasts] : toasts
            return { ok: !refusal && !toasts.some(text => text.startsWith('Erro:')), messages, state: snapshot() }
        }

        return registerScreenController('billing', {
            billingId: deps.billingId,
            getState: snapshot,
            setPayment: (method, installments) =>
                run(async () => {
                    const d = depsRef.current
                    const normalized = String(method || '').toUpperCase()
                    if (!AI_PAYMENT_METHODS.includes(normalized)) {
                        return `Forma ${method} não é lançada por você (só PIX, CREDIT_CARD e DEBIT_CARD).`
                    }
                    if (!d.availableMethods.includes(normalized)) {
                        return `A forma ${normalized} não está disponível para esta empresa.`
                    }
                    if (d.parts.some(part => part.method === normalized)) {
                        return `${normalized} já está numa das partes. Cada forma aparece uma vez.`
                    }
                    const count = normalized === 'CREDIT_CARD' ? Math.max(1, Math.round(Number(installments) || 1)) : 1
                    if (normalized === 'CREDIT_CARD') {
                        // A mesma cotação do bloco de parcelas da tela, sobre o valor da principal.
                        const quote = await getPaymentOptions(d.principalAmount, ['CREDIT_CARD'])
                        const offered = quote.success ? (quote.result?.options ?? []) : []
                        if (!offered.some(option => option.installments === count)) {
                            return `Cartão em ${count}x não é oferecido para ${(d.principalAmount / 100).toFixed(2)}.`
                        }
                    }
                    d.setPaymentMethod(normalized, count > 1 ? count : null)
                }),
            setParts: parts =>
                run(() => {
                    const d = depsRef.current
                    const total = d.principalAmount + d.parts.reduce((sum, part) => sum + part.amount, 0)
                    const normalized = parts.map(part => ({
                        method: String(part.method || '').toUpperCase(),
                        amount: Math.round(Number(part.amount)),
                    }))
                    if (normalized.length === 0) {
                        d.setParts([])
                        return
                    }
                    // Cartão na principal e Pix como parte: só a forma principal leva parcelas
                    // (`installmentCount`); o cartão como parte sairia sempre à vista.
                    if (d.paymentMethod !== 'CREDIT_CARD') {
                        return 'Para dividir, ponha o cartão de crédito como forma principal (billing_set_payment) e o Pix como parte.'
                    }
                    if (normalized.length !== 1 || normalized[0].method !== 'PIX') {
                        return 'Você divide só em cartão (principal) + Pix (parte). Outra combinação: use ask_supervisor.'
                    }
                    const sum = normalized.reduce((acc, part) => acc + part.amount, 0)
                    if (normalized.some(part => !Number.isFinite(part.amount) || part.amount <= 0)) {
                        return 'Cada parte precisa de um valor maior que zero, em centavos.'
                    }
                    if (sum >= total) return 'As partes não podem somar o total: sobra nada para o cartão.'
                    d.setParts(normalized)
                }),
            save: async () => {
                const { result, toasts } = await collectToasts(async (): Promise<BillingSaveResult> => {
                    const d = depsRef.current
                    if (d.busy) return { ok: false, messages: ['Erro: a tela ainda está ocupada.'] }
                    if (!d.paymentMethod) return { ok: false, messages: ['Erro: escolha a forma principal (billing_set_payment).'] }
                    const expected = { method: d.paymentMethod, parts: d.parts.length }
                    await d.save()
                    // O salvar navega para o atendimento: o que vale é o que ficou gravado.
                    const response = await getBilling(d.billingId)
                    const billing: any = response.success ? response.result : null
                    if (!billing || billing.paymentMethod !== expected.method) return { ok: false, messages: [] }
                    const children: any[] = (billing.adjustments ?? []).filter(
                        (child: any) => child?.metadata?.paymentSplit === true && child.status !== 'CANCELLED',
                    )
                    if (children.length !== expected.parts) return { ok: false, messages: [] }
                    return {
                        ok: true,
                        messages: [],
                        payments: [billing, ...children].map(entry => ({
                            billingId: entry.id,
                            method: entry.paymentMethod ?? null,
                            installments: entry.installmentCount ?? null,
                            amount: entry.amount,
                            checkoutUrl: checkoutUrlOf(entry),
                            pixCopyPaste: entry.pixCopyPaste ?? null,
                        })),
                    }
                })
                return { ...result, messages: [...result.messages, ...toasts], ok: result.ok && !toasts.some(text => text.startsWith('Erro:')) }
            },
        })
    }, [deps.enabled, deps.billingId])
}
